import {
  HomeIcon,
  UserGroupIcon,
  TrophyIcon,
  RectangleStackIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  PlayIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  TrophyIcon as TrophyIconSolid,
  RectangleStackIcon as RectangleStackIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  PlayIcon as PlayIconSolid,
  AcademicCapIcon as AcademicCapIconSolid,
} from '@heroicons/react/24/solid';

/* Afgeleid van een echt heroicon: alle v2-iconen delen dezelfde
   ForwardRefExoticComponent-signatuur, dus dit blijft exact kloppen. */
type HeroIcon = typeof HomeIcon;

export interface NavItem {
  id: string;
  /** Vertaalsleutel is de `id`: `nav:<id>`. */
  href: string;
  icon: HeroIcon;
  /** Gevuld icoon voor de actieve tab in de mobiele onderbalk. */
  iconSolid: HeroIcon;
  /** Alleen Dashboard matcht exact; de rest matcht ook op subroutes. */
  exact?: boolean;
  /** Toont een bèta-teken naast het label, in de sidebar en in het Meer-paneel. */
  beta?: boolean;
}

/**
 * Enige bron van waarheid voor de navigatie. De desktop-sidebar rendert deze lijst
 * in volgorde; de mobiele onderbalk splitst hem via `primaryTabIds` / `moreNavItems`.
 */
export const navigation: NavItem[] = [
  { id: 'dashboard', href: '/coach', icon: HomeIcon, iconSolid: HomeIconSolid, exact: true },
  { id: 'wedstrijden', href: '/coach/wedstrijden', icon: TrophyIcon, iconSolid: TrophyIconSolid },
  { id: 'spelers', href: '/coach/spelers', icon: UserGroupIcon, iconSolid: UserGroupIconSolid },
  { id: 'formaties', href: '/coach/formaties', icon: RectangleStackIcon, iconSolid: RectangleStackIconSolid },
  { id: 'oefeningen', href: '/coach/oefeningen', icon: AcademicCapIcon, iconSolid: AcademicCapIconSolid },
  { id: 'analyse', href: '/coach/analyse', icon: PlayIcon, iconSolid: PlayIconSolid },
  { id: 'statistieken', href: '/coach/statistieken', icon: ChartBarIcon, iconSolid: ChartBarIconSolid },
  { id: 'instellingen', href: '/coach/instellingen', icon: Cog6ToothIcon, iconSolid: Cog6ToothIconSolid },
];

/** De vier items die een eigen tab in de onderbalk krijgen. */
export const primaryTabIds = ['dashboard', 'wedstrijden', 'spelers', 'statistieken'];

/** Id van de vijfde tab; ook de terugvalwaarde van `getActiveTabId`. */
export const MORE_TAB_ID = 'meer';

export const primaryNavItems = navigation.filter((item) => primaryTabIds.includes(item.id));

/** Alles wat niet in de balk past, komt in het Meer-paneel. */
export const moreNavItems = navigation.filter((item) => !primaryTabIds.includes(item.id));

/**
 * Bepaalt welke tab oplicht voor een pad.
 *
 * Matcht op segmentgrens (`href` zelf of `href + '/'`) in plaats van een kale
 * `startsWith`, zodat een toekomstige route als `/coach/spelersgroepen` niet per
 * ongeluk Spelers activeert. Onbekende `/coach/*`-routes vallen terug op Meer,
 * zodat er nooit een pagina is waar geen enkele tab oplicht.
 */
export function getActiveTabId(pathname: string): string {
  const path = pathname.replace(/\/+$/, '') || '/';

  const match = navigation.find((item) =>
    item.exact ? path === item.href : path === item.href || path.startsWith(`${item.href}/`)
  );

  return match && primaryTabIds.includes(match.id) ? match.id : MORE_TAB_ID;
}
